import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Search, Check, Plus, Loader2 } from 'lucide-react';
import type { D2DArea } from '@/types/d2d';

interface AddAreasToFolderDialogProps {
  folderName: string;
  existingAreaIds: string[];
  allAreas: D2DArea[];
  onAddAreas: (areaIds: string[]) => Promise<void>;
  trigger?: React.ReactNode;
}

export const AddAreasToFolderDialog = ({ 
  folderName, 
  existingAreaIds, 
  allAreas, 
  onAddAreas,
  trigger 
}: AddAreasToFolderDialogProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAreaIds, setSelectedAreaIds] = useState<string[]>([]);
  const [isAdding, setIsAdding] = useState(false);

  const resetDialog = () => {
    setSearchQuery('');
    setSelectedAreaIds([]);
    setIsAdding(false);
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      resetDialog();
    }
  };

  const handleToggleArea = (areaId: string) => {
    setSelectedAreaIds(prev => 
      prev.includes(areaId)
        ? prev.filter(id => id !== areaId)
        : [...prev, areaId]
    );
  };

  const handleAdd = async () => {
    if (selectedAreaIds.length === 0) return;
    
    setIsAdding(true);
    try {
      await onAddAreas(selectedAreaIds);
      handleOpenChange(false);
    } catch (error) {
      console.error('Error adding areas to folder:', error);
    } finally {
      setIsAdding(false);
    }
  };

  // Filter areas: exclude already in folder, apply search
  const availableAreas = allAreas.filter(area => !existingAreaIds.includes(area.id));
  
  const filteredAreas = availableAreas.filter(area => {
    if (!searchQuery.trim()) return true;
    
    const query = searchQuery.toLowerCase().trim();
    const nameMatch = area.name.toLowerCase().includes(query);
    const plzMatch = area.plz?.toLowerCase().includes(query);
    
    return nameMatch || plzMatch;
  });

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
            <Plus className="h-4 w-4" />
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Gebiete zu "{folderName}" hinzufügen</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="area-search-add">Gebiete suchen</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="area-search-add"
                placeholder="PLZ oder Name eingeben..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                autoFocus
              />
            </div>
          </div>

          <div className="max-h-[300px] overflow-y-auto space-y-2">
            {filteredAreas.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">
                {searchQuery 
                  ? 'Keine Gebiete gefunden' 
                  : availableAreas.length === 0 
                    ? 'Alle Gebiete sind bereits im Ordner' 
                    : 'Keine Gebiete vorhanden'}
              </p>
            ) : (
              filteredAreas.map((area) => (
                <div
                  key={area.id}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    selectedAreaIds.includes(area.id)
                      ? 'bg-primary/10 border-primary'
                      : 'hover:bg-muted/50'
                  }`}
                  onClick={() => handleToggleArea(area.id)}
                >
                  <Checkbox
                    checked={selectedAreaIds.includes(area.id)}
                    onCheckedChange={() => handleToggleArea(area.id)}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{area.name}</p>
                    {area.plz && (
                      <p className="text-sm text-muted-foreground">{area.plz}</p>
                    )}
                    {area.description && !area.description.includes('Migriert aus altem System') && (
                      <p className="text-xs text-muted-foreground/70 truncate">{area.description}</p>
                    )}
                  </div>
                  {selectedAreaIds.includes(area.id) && (
                    <Check className="h-4 w-4 text-primary shrink-0" />
                  )}
                </div>
              ))
            )}
          </div>

          {selectedAreaIds.length > 0 && (
            <p className="text-sm text-muted-foreground text-center">
              {selectedAreaIds.length} {selectedAreaIds.length === 1 ? 'Gebiet' : 'Gebiete'} ausgewählt
            </p>
          )}

          <Button
            onClick={handleAdd}
            disabled={selectedAreaIds.length === 0 || isAdding}
            className="w-full gap-2"
          >
            {isAdding ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Wird hinzugefügt...
              </>
            ) : (
              <>
                <Check className="h-4 w-4" />
                Hinzufügen ({selectedAreaIds.length})
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
